import { API_URL } from "@/constants";
import { isStageActiveNow, parsePeruvianDate } from "@/lib/format";
import { useMemo } from "react";
import { dniFromRuc } from "@/lib/domain-constants";
import type {
  BidAnalysis,
  BidRequirement,
  ProcedureAction,
} from "@/types/domain";

function parseDocs(raw: any): any[] {
  if (!raw) return [];
  let docs = raw;
  if (typeof docs === "string") {
    try {
      docs = JSON.parse(docs);
    } catch {
      return [];
    }
  }
  return Array.isArray(docs) ? docs : [];
}

// Groups a matched interest's matches by file, parsing the "desc > Cant: x >
// Total: y" category path into displayable fields.
function groupInterestMatches(interest: any) {
  const fileGroups = new Map<
    string,
    {
      priceitemId: number | null;
      description: string;
      qty: string;
      total: string;
    }[]
  >();
  for (const m of interest.matches ?? []) {
    if (!fileGroups.has(m.fileName)) fileGroups.set(m.fileName, []);
    const parts = String(m.categoryPath).split(">");
    const description = parts[0]?.trim() || m.categoryPath;
    const qty = parts[1] ? parts[1].replace("Cant:", "").trim() : "";
    const total = parts[2] ? parts[2].replace("Total:", "").trim() : "";
    fileGroups.get(m.fileName)!.push({
      priceitemId: m.priceitem_id ?? null,
      description,
      qty,
      total,
    });
  }
  return {
    interestName: interest.interestName,
    count: interest.count,
    files: Array.from(fileGroups.entries()).map(([fileName, items]) => ({
      fileName,
      items,
    })),
  };
}

/** Las bases del proceso, la definitiva primero. Espeja `BASES_FILE_TYPES`. */
const BID_DOCUMENT_TYPES = ["BASES_INTEGRADAS", "BASES_ADMINISTRATIVAS"];

/**
 * Da forma a la data cruda del detalle de una licitación para la UI: documentos
 * de origen, cronograma, ítems de precio, intereses coincidentes (deduplicados)
 * y el ganador. Aísla todo el modelado del componente de render.
 */
const onlyDigits = (value: unknown) => String(value ?? "").replace(/\D/g, "");
const normalizedName = (value: unknown) => String(value ?? "").trim().toUpperCase();

/** Si quien tramita las fianzas es este integrante: por RUC y, si no calza, por nombre. */
const isGuaranteeManager = (manager: any, member: any) =>
  !!manager &&
  ((!!manager.ruc && onlyDigits(manager.ruc) === onlyDigits(member.ruc)) ||
    (!!manager.name && normalizedName(manager.name) === normalizedName(member.name)));

/**
 * Lo que se sabe de la oferta técnica de un consorcio: si ya se leyó y qué dijo su
 * promesa de consorcio. `null` si todavía no se detectaron sus documentos.
 */
function technicalOfferSummary(offer: any, files: any[]): any {
  if (!offer?.documents?.length) return null;
  const read = offer.technicalOffer;
  const status = !read?.status
    ? "pending"
    : read.status === "FAILED"
      ? "failed"
      : read.guaranteeManager
        ? "identified"
        : "notIdentified";
  // El archivo se registra como "<fileCode> - <nombre>".
  const version = read?.fileCode
    ? files
        .find(
          (f: any) =>
            f.contractingfiletype?.contractingfiletype_name === "TECHNICAL_OFFER" &&
            String(f.contractingfile_name ?? "").startsWith(`${read.fileCode} - `),
        )
        ?.contractingfileversion?.[0]
    : null;
  return {
    status,
    guaranteeManager: read?.guaranteeManager ?? null,
    address: read?.commonAddress ?? null,
    email: read?.email ?? null,
    fileUrl: version?.contractingfileversion_fileurl ?? null,
    page: read?.page ?? null,
    pages: version?.contractingfileversion_data?.paginas_incluidas ?? null,
  };
}

/**
 * Suma a un consorcio ganador lo que dice su oferta en el SEACE: el % de cada
 * integrante, el representante legal y, de su oferta técnica, quién tramita las
 * fianzas. Llega con la buena pro, antes que el contrato de consorcio.
 */
function attachConsortiumOffer(group: any, files: any[]): any {
  const offer = group.offer;
  const technicalOffer = technicalOfferSummary(offer, files);
  const manager = technicalOffer?.guaranteeManager;
  const shareByRuc = new Map<string, number | null>(
    (offer?.members ?? []).map((m: any) => [onlyDigits(m.ruc), m.participation ?? null]),
  );
  const representative = offer?.representative;
  return {
    ...group,
    members: group.members.map((m: any) => {
      const share = shareByRuc.get(onlyDigits(m.ruc)) ?? null;
      const handlesGuarantees =
        manager?.role === "INTEGRANTE" && isGuaranteeManager(manager, m);
      return {
        ...m,
        share,
        shareSource: share != null ? "oferta" : null,
        handlesGuarantees,
        guaranteeSource: handlesGuarantees ? "propuesta técnica" : null,
      };
    }),
    representative: representative
      ? {
          name: representative.name,
          document: representative.documentNumber
            ? `${representative.documentType ?? "DNI"} ${representative.documentNumber}`
            : null,
          source: "oferta",
        }
      : null,
    representativeHandlesGuarantees: manager?.role === "REPRESENTANTE_COMUN",
    representativeGuaranteeSource:
      manager?.role === "REPRESENTANTE_COMUN" ? "propuesta técnica" : null,
    technicalOffer,
  };
}

/**
 * Suma a cada consorcio ganador lo que se leyó de su contrato de consorcio: el %
 * de cada integrante, el representante común y quién tramita las fianzas. Se
 * empareja por RUC; con un solo consorcio y un solo contrato, aunque no calce. El
 * contrato manda sobre la oferta: es lo que se firmó; lo que no trae, lo pone la oferta.
 */
function attachConsortiumAgreements(c: any, rawGroups: any[]): any[] {
  const files = c?.contractingfile ?? [];
  const groups = rawGroups.map((group) =>
    group.type === "consorcio" ? attachConsortiumOffer(group, files) : group,
  );
  const agreements = files
    .filter(
      (f: any) =>
        f.contractingfiletype?.contractingfiletype_name === "CONSORTIUM_AGREEMENT",
    )
    .map((f: any) => f.contractingfileversion?.[0])
    .filter(
      (v: any) =>
        v?.contractingfileversion_processingwasok &&
        v.contractingfileversion_data?.es_contrato_de_consorcio,
    )
    .map((v: any) => v.contractingfileversion_data);
  const consortia = groups.filter((group) => group.type === "consorcio");

  return groups.map((group) => {
    if (group.type !== "consorcio") return group;
    const rucs = new Set(group.members.map((m: any) => onlyDigits(m.ruc)));
    const agreement =
      agreements.find((a: any) =>
        (a.integrantes ?? []).some((i: any) => rucs.has(onlyDigits(i.ruc))),
      ) ?? (consortia.length === 1 && agreements.length === 1 ? agreements[0] : null);
    if (!agreement) return group;

    const shareByRuc = new Map<string, number | null>(
      (agreement.integrantes ?? []).map((i: any) => [
        onlyDigits(i.ruc),
        i.porcentaje ?? null,
      ]),
    );
    const commonRepresentative = agreement.representante_comun;
    const manager = agreement.responsable_garantias
      ? {
          name: agreement.responsable_garantias.nombre,
          ruc: agreement.responsable_garantias.ruc,
          role: agreement.responsable_garantias.rol,
        }
      : null;
    return {
      ...group,
      members: group.members.map((m: any) => {
        const agreedShare = shareByRuc.get(onlyDigits(m.ruc)) ?? null;
        const handlesGuarantees =
          manager?.role === "INTEGRANTE" && isGuaranteeManager(manager, m);
        return {
          ...m,
          ...(agreedShare != null ? { share: agreedShare, shareSource: "contrato" } : {}),
          // Si el contrato no dice quién tramita las fianzas, queda lo de la propuesta técnica.
          ...(manager
            ? { handlesGuarantees, guaranteeSource: handlesGuarantees ? "contrato" : null }
            : {}),
        };
      }),
      representative: commonRepresentative?.nombre
        ? {
            name: commonRepresentative.nombre,
            document: commonRepresentative.dni ? `DNI ${commonRepresentative.dni}` : null,
            source: "contrato",
          }
        : (group.representative ?? null),
      ...(manager
        ? {
            representativeHandlesGuarantees: manager.role === "REPRESENTANTE_COMUN",
            representativeGuaranteeSource:
              manager.role === "REPRESENTANTE_COMUN" ? "contrato" : null,
          }
        : {}),
    };
  });
}

export function useContractingDetailModel(c: any, searchProfileId?: number) {
  // PRICE_RELATION source documents, mapped to the DocCard shape.
  const sourceFiles = useMemo(() => {
    return (c?.contractingfile ?? [])
      .filter(
        (f: any) =>
          f.contractingfiletype?.contractingfiletype_name === "PRICE_RELATION",
      )
      .map((f: any) => {
        const v = f.contractingfileversion?.[0];
        const tmppath = v?.contractingfileversion_tmppath as string | undefined;
        return {
          title: f.contractingfile_name,
          format: (
            f.contractingfile_name?.split(".").pop() ?? ""
          ).toLowerCase(),
          datePublished: v?.contractingfileversion_publicationdate ?? null,
          // URL firmada de R2; el fallback a /files/ es transicional mientras
          // se migran los archivos históricos al bucket.
          url:
            v?.contractingfileversion_fileurl ??
            (tmppath
              ? `${API_URL}/files/${tmppath.split("/app-files/")[1]}`
              : null),
        };
      });
  }, [c]);

  /**
   * Los requisitos que la IA leyó de las bases, con el documento del que salieron.
   * Las integradas mandan sobre las administrativas, y cuál de las dos es se dice
   * en pantalla: una administrativa todavía puede cambiar tras la absolución de
   * consultas. `null` cuando no hay bases legibles — ahí la pestaña no se pinta.
   */
  const bidRequirements = useMemo(() => {
    const files = c?.contractingfile ?? [];

    for (const typeName of BID_DOCUMENT_TYPES) {
      const file = files.find(
        (f: any) => f.contractingfiletype?.contractingfiletype_name === typeName,
      );
      const version = file?.contractingfileversion?.[0];
      const data = version?.contractingfileversion_data;
      // Una lectura fallida deja `data` en null o `processingwasok` en false: en
      // los dos casos se prueba el siguiente tipo y, si no hay, no hay pestaña.
      if (!version?.contractingfileversion_processingwasok || !data) continue;

      const tmppath = version.contractingfileversion_tmppath as
        | string
        | undefined;
      return {
        isFinal: typeName === "BASES_INTEGRADAS",
        // El OECE las lista aunque nosotros no las hayamos bajado. Sirve para no
        // decirle al usuario que los requisitos "pueden cambiar" cuando ya
        // cambiaron y lo que está viendo es la versión vieja.
        finalIsPublished: parseDocs(
          c?.contractingversion?.[0]?.contractingversion_documents,
        ).some((doc: any) => doc?.title?.trim() === "Bases Integradas"),
        fileName: file.contractingfile_name as string,
        publishedAt: version.contractingfileversion_publicationdate ?? null,
        url:
          version.contractingfileversion_fileurl ??
          (tmppath
            ? `${API_URL}/files/${tmppath.split("/app-files/")[1]}`
            : null),
        totalPages: (data.totalPaginas ?? null) as number | null,
        totalScore: (data.puntajeTotal ?? null) as number | null,
        // Los INFORMATIVO se descartan acá y no al pintar, para que el
        // contador de la pestaña cuente lo mismo que la tabla muestra. Son los
        // anexos y declaraciones que presenta cualquier postor: no separan
        // empresas, así que no ayudan a decidir si vale la pena postular.
        requirements: ((data.requisitos ?? []) as BidRequirement[]).filter(
          (requirement) => requirement.categoria !== "INFORMATIVO",
        ),
      };
    }

    return null;
  }, [c]);

  /** El cruce contra los documentos de la empresa, si ya se corrió. */
  const bidAnalysis = useMemo(
    () => (c?.bidAnalysis ?? null) as BidAnalysis | null,
    [c],
  );

  const documents = useMemo(
    () => parseDocs(c?.contractingversion?.[0]?.contractingversion_documents),
    [c],
  );

  // Acciones del SEACE, la más reciente primero.
  const procedureActions: ProcedureAction[] = useMemo(() => {
    const actions = c?.contractingversion?.[0]?.contractingversion_actions;
    if (!Array.isArray(actions)) return [];
    return [...actions].sort((a, b) =>
      String(b.publishedAt).localeCompare(String(a.publishedAt)),
    );
  }, [c]);

  // Cuándo cambió el cronograma: la versión activa del schedule.json, porque el
  // hash solo crea versión nueva si el contenido cambió.
  const scheduleUpdatedAt: string | null = useMemo(
    () =>
      c?.contractingfile?.find(
        (f: any) => f.contractingfiletype?.contractingfiletype_name === "SCHEDULE",
      )?.contractingfileversion?.[0]?.contractingfileversion_createdat ?? null,
    [c],
  );

  const schedule: any[] = useMemo(() => {
    const stages =
      c?.contractingversion?.[0]?.contractingversion_schedule ??
      c?.contractingversion_schedule ??
      [];
    // La etapa vigente se calcula aquí desde las fechas (en vivo), no desde un
    // flag del scraper que quedaría congelado al momento del scrape.
    const withActive = stages.map((s: any) => ({
      ...s,
      isActive: isStageActiveNow(s.fechaInicio, s.fechaFin),
      isLastActivity: false,
    }));
    if (withActive.some((s: any) => s.isActive)) return withActive;

    // Nada en curso (una etapa vencida que aún no reprograman, o un hueco entre
    // etapas): se marca la última que empezó, para que se vea dónde se quedó.
    let lastStarted: any = null;
    let lastStartedAt: number | null = null;
    for (const stage of withActive) {
      const startedAt = parsePeruvianDate(stage.fechaInicio)?.valueOf();
      if (startedAt === undefined || startedAt > Date.now()) continue;
      if (lastStartedAt === null || startedAt >= lastStartedAt) {
        lastStarted = stage;
        lastStartedAt = startedAt;
      }
    }
    if (lastStarted) lastStarted.isLastActivity = true;
    return withActive;
  }, [c]);

  const priceItems = useMemo(() => {
    const items: any[] = [];
    for (const file of c?.contractingfile ?? []) {
      const version = file.contractingfileversion?.[0];
      for (const it of version?.contractingpriceitem ?? []) {
        const isSaved = (it.priceitemusers?.length ?? 0) > 0;
        const hasProfileMatch = (it.priceitemsearchprofiles?.length ?? 0) > 0;
        items.push({
          id: it.priceitem_id,
          description: it.priceitem_description,
          unit: it.priceitem_unit || "N/A",
          quantity: Number(it.priceitem_quantity ?? 1),
          unitPrice: Number(it.priceitem_unitprice ?? 0),
          totalPrice: Number(it.priceitem_totalprice ?? 0),
          fileName: file.contractingfile_name,
          category: it.priceitemcategory?.priceitemcategory_name ?? null,
          isSaved,
          hasMatch: searchProfileId ? hasProfileMatch : isSaved,
          matchedKeyword: searchProfileId
            ? (it.priceitemsearchprofiles?.[0]
                ?.contractingpriceitemsearchprofile_matchedkeyword ?? null)
            : (it.priceitemusers?.[0]?.contractinguserpriceitem_keyword ??
              null),
          // Keyword the user saved the item with, independent of any active
          // search profile (used by the "Ítems en seguimiento" section).
          savedKeyword:
            it.priceitemusers?.[0]?.contractinguserpriceitem_keyword ?? null,
        });
      }
    }
    return items;
  }, [c, searchProfileId]);

  // Matched interests grouped by every search profile that matched this
  // contracting, so the user can see all their profiles at once.
  const matchedProfiles = useMemo(() => {
    const profiles: any[] = c?.matchedProfiles ?? [];
    return profiles.map((p) => ({
      searchprofileId: p.searchprofile_id,
      searchprofileName: p.searchprofile_name,
      count: p.count,
      interests: (p.interests ?? []).map(groupInterestMatches),
    }));
  }, [c]);

  const activeProfileName = useMemo(
    () =>
      matchedProfiles.find((p) => p.searchprofileId === searchProfileId)
        ?.searchprofileName ?? null,
    [matchedProfiles, searchProfileId],
  );

  // Flatten profile → interest → file → items into a single deduplicated list.
  // The same price item can match several profiles/keywords, so we collapse it
  // into one row and collect the matching keywords + profiles as chips.
  const matchedItems = useMemo(() => {
    const map = new Map<
      string,
      {
        priceitemId: number | null;
        description: string;
        qty: string;
        total: string;
        interests: Set<string>;
        profiles: Set<string>;
      }
    >();
    for (const profile of matchedProfiles) {
      for (const interest of profile.interests) {
        for (const file of interest.files) {
          for (const item of file.items) {
            const key =
              item.priceitemId != null
                ? `id-${item.priceitemId}`
                : `d-${item.description}|${item.qty}|${item.total}`;
            let entry = map.get(key);
            if (!entry) {
              entry = {
                priceitemId: item.priceitemId,
                description: item.description,
                qty: item.qty,
                total: item.total,
                interests: new Set(),
                profiles: new Set(),
              };
              map.set(key, entry);
            }
            entry.interests.add(interest.interestName);
            entry.profiles.add(profile.searchprofileName);
          }
        }
      }
    }
    return Array.from(map.values());
  }, [matchedProfiles]);

  // Builds a member entry from a `company` record, pulling extra contact
  // info from its OSCE / SUNAT extracted data when available.
  const fromCompany = (company: any) => {
    const extracted = company?.company_extracteddata;
    const osce = extracted?.osce;
    const sunat = extracted?.consultarucsunat;
    const basicInfo = osce?.basicInfo ?? null;

    const address =
      sunat?.["Domicilio Fiscal"] ??
      osce?.resumen?.datosSunat?.domicilioFiscal ??
      basicInfo?.proveedorT01?.domicilioFiscal ??
      null;
    const email =
      basicInfo?.proveedorT01?.emails?.join(", ") ??
      basicInfo?.datosGenerales?.correoElectronico ??
      null;
    const phone =
      basicInfo?.proveedorT01?.telefonos?.join(", ") ??
      basicInfo?.datosGenerales?.telefono ??
      null;

    // Persona natural: el RUC `10…` lleva el DNI embebido. Si SUNAT trajo el
    // "Tipo de Documento" se usa ese (es la fuente autoritativa); si no, se
    // deriva del RUC, que siempre está.
    const ruc = company?.company_ruc;
    const dniSunat = String(sunat?.["Tipo de Documento"] ?? "").match(
      /DNI\s+(\d{8})/,
    )?.[1];
    const dni = dniSunat ?? dniFromRuc(ruc);

    return {
      companyId: company?.company_id ?? null,
      name: company?.company_name,
      ruc,
      dni,
      isPersonaNatural: dni != null,
      address,
      email,
      phone,
    };
  };

  // Agrupa los ítems de la licitación por ganador (consorcio o empresa), con su
  // contacto. Los ítems aún sin adjudicar caen en `unassignedItems`. Compartido
  // por ambos layouts (obra: fusionado en Buena Pro; bien/servicio: sección
  // protagonista). El match sigue el patrón de price items (solo con perfil).
  const { winnerGroups, unassignedItems } = useMemo(() => {
    const mapItem = (it: any) => ({
      id: it.contractingitem_id,
      position: it.contractingitem_position,
      description: it.contractingitem_description,
      referentialAmount: Number(it.contractingitem_referentialamount ?? 0),
      awardedAmount:
        it.contractingitem_awardedamount != null
          ? Number(it.contractingitem_awardedamount)
          : null,
      result: it.contractingitem_result ?? null,
      hasMatch: (it.itemsearchprofiles?.length ?? 0) > 0,
      matchedKeyword:
        it.itemsearchprofiles?.[0]
          ?.contractingitemsearchprofile_matchedkeyword ?? null,
    });

    const groups = new Map<
      string,
      {
        key: string;
        name: string;
        // Solo en ganador único: el consorcio no tiene ficha propia.
        companyId?: number;
        type: "empresa" | "consorcio";
        members: any[];
        items: any[];
        // Lo leído de la oferta del consorcio en el SEACE (% y representante).
        offer?: any;
        // El RUC del consorcio, si ya se conoce (lo trae la oferta o el reporte).
        ruc?: string | null;
      }
    >();
    const unassigned: any[] = [];

    for (const it of c?.contractingitem ?? []) {
      const item = mapItem(it);
      if (it.consortium) {
        const key = `c-${it.consortium.consortium_id}`;
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            name: it.consortium.consortium_name,
            type: "consorcio",
            members: (it.consortium.consortiummembers ?? []).map((m: any) =>
              fromCompany(m.company),
            ),
            items: [],
            offer: it.consortium.consortium_extracteddata?.seaceOffer ?? null,
            ruc: it.consortium.consortium_ruc ?? null,
          });
        }
        groups.get(key)!.items.push(item);
      } else if (it.company) {
        const key = `e-${it.company.company_id}`;
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            name: it.company.company_name,
            companyId: it.company.company_id,
            type: "empresa",
            members: [fromCompany(it.company)],
            items: [],
          });
        }
        groups.get(key)!.items.push(item);
      } else {
        unassigned.push(item);
      }
    }

    return {
      winnerGroups: attachConsortiumAgreements(c, Array.from(groups.values())),
      unassignedItems: unassigned,
    };
  }, [c]);

  // Quiénes presentaron oferta, del reporte de presentación de propuestas.
  // `null` si el reporte todavía no se leyó.
  const bidders = useMemo(() => {
    const report = (c?.contractingfile ?? []).find(
      (f: any) =>
        f.contractingfiletype?.contractingfiletype_name === "BID_SUBMISSION_REPORT",
    );
    const version = report?.contractingfileversion?.[0];
    if (!version?.contractingfileversion_processingwasok) return null;

    // Un consorcio postor llega con el RUC del integrante que presentó la oferta.
    const winnerRucs = new Set(
      winnerGroups.flatMap((g: any) => g.members.map((m: any) => onlyDigits(m.ruc))),
    );
    const winnerNames = new Set(winnerGroups.map((g: any) => normalizedName(g.name)));
    return (version.contractingfileversion_data?.postores ?? []).map((p: any) => ({
      item: String(p.nro_item ?? ""),
      name: p.nombre,
      ruc: p.ruc,
      submittedAt: `${p.fecha} ${p.hora}`,
      form: p.forma,
      won:
        winnerRucs.has(onlyDigits(p.ruc)) || winnerNames.has(normalizedName(p.nombre)),
    }));
  }, [c, winnerGroups]);

  return {
    sourceFiles,
    documents,
    schedule,
    procedureActions,
    scheduleUpdatedAt,
    priceItems,
    matchedProfiles,
    activeProfileName,
    matchedItems,
    winnerGroups,
    unassignedItems,
    bidders,
    bidRequirements,
    bidAnalysis,
  };
}
